import { motion } from "framer-motion";
import GaugeChart from "react-gauge-component";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Math.abs(value))
    .replace("ZAR", "R");
};

// Color helper: negative = red, positive = blue, zero = black
const getValueColor = (value) => {
  if (value > 0) return "#0891B2"; // Blue/Teal - positive
  if (value < 0) return "#DC2626"; // Red - negative
  return "#0F172A"; // Black - neutral/zero
};

const VehicleGauge = ({ data, index }) => {
  const percent = Math.min(data.percent_to_target, 130);
  const isAboveTarget = data.percent_to_target >= 100;

  const getPercentColor = () => {
    if (isAboveTarget) return "#0891B2";
    return "#DC2626";
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
      className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-center shadow-sm"
      data-testid={`gauge-${data.vehicle_name.toLowerCase()}`}
    >
      {/* Vehicle Name */}
      <div className="font-semibold text-[#0F172A] mb-2">{data.vehicle_name}</div>

      {/* Gauge */}
      <div className="w-full max-w-[160px] mx-auto">
        <GaugeChart
          id={`gauge-${data.vehicle_name}`}
          value={percent}
          minValue={0}
          maxValue={130}
          type="semicircle"
          arc={{
            width: 0.2,
            padding: 0.02,
            cornerRadius: 1,
            subArcs: [
              { limit: 90, color: "#DC2626", showTick: true },
              { limit: 100, color: "#D97706", showTick: true },
              { limit: 130, color: "#0891B2", showTick: true },
            ],
          }}
          pointer={{
            type: "needle",
            color: "#64748B",
            length: 0.8,
            width: 15,
            elastic: true,
            animationDelay: 200,
            animationDuration: 1000,
          }}
          labels={{
            valueLabel: {
              hide: true,
            },
            tickLabels: {
              type: "outer",
              ticks: [
                { value: 0 },
                { value: 50 },
                { value: 100 },
                { value: 130 },
              ],
              defaultTickValueConfig: {
                style: {
                  fontSize: "8px",
                  fill: "#94A3B8",
                },
                formatTextValue: (value) => `${value}%`,
              },
            },
          }}
        />
      </div>

      {/* Percentage Display */}
      <div className="text-xl font-bold font-mono -mt-1 mb-3" style={{ color: getPercentColor() }}>
        {data.percent_to_target.toFixed(1)}%
      </div>

      {/* Details */}
      <div className="space-y-1 text-sm">
        <div>
          <span className="text-[#64748B]">Actual: </span>
          <span className="text-[#0891B2] font-mono">{formatCurrency(data.mtd_actual)}</span>
        </div>
        <div>
          <span className="text-[#64748B]">Target: </span>
          <span className="text-[#0F172A] font-mono">{formatCurrency(data.mtd_target)}</span>
        </div>
        <div>
          <span className="text-[#64748B]">Variance: </span>
          <span 
            className="font-mono"
            style={{ color: getValueColor(data.variance) }}
          >
            {data.variance < 0 ? "-" : ""}{formatCurrency(data.variance)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default function GaugeCluster({ vehicles }) {
  return (
    <div data-testid="gauge-cluster">
      {/* Section Header */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-[#0F172A]">Vehicle Performance</h2>
      </div>

      {/* Gauges Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {vehicles.map((vehicle, index) => (
          <VehicleGauge
            key={vehicle.vehicle_name}
            data={vehicle}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}
